import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';

import {
    Box,
    DialogContent,
    Typography,
    FormControl,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    SelectChangeEvent,
    Chip,
} from '@mui/material';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {
    clearProject,
    createNewEmptyProject,
    importAllProjectManagers,
    importProjectForProjectForm,
    saveProject,
    selectClient,
    updateProjectName,
    updateProjectProjectManagers,
} from '../../../redux/actions/projectActions';
import { RootState } from '../../../redux/reducers';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { useLocation } from 'react-router-dom';
import { importClients } from '../../../redux/actions/clientActions';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

export interface ProjectFormProps {
    open: boolean;
    closeForm(): void;
    currProjectId?: number;
}

export default function ProjectForm(props: ProjectFormProps): ReactElement {
    const [isUpdate, setIsUpdate] = useState<boolean>(false);

    const project = useAppSelector((state: RootState) => state.projectForm.project);
    const clients = useAppSelector((state) => state.clients.clients);
    const allProjectManagers = useAppSelector((state) => state.projectForm.allProjectManagers);

    const saveStatus = useAppSelector((state: RootState) => state.projectForm.saveStatus);

    const { open, closeForm, currProjectId } = props;

    const dispatch = useAppDispatch();

    const params = useParams();
    const location = useLocation();
    const path = location.pathname;
    const projectId = params.projectId;

    useEffect(() => {
        if (open && !!path) {
            if (!!projectId && !isNaN(+projectId) && +projectId > 0) {
                dispatch(importProjectForProjectForm(+projectId));
                setIsUpdate(true);
            } else {
                if (!!currProjectId) {
                    dispatch(importProjectForProjectForm(currProjectId));
                    setIsUpdate(true);
                } else {
                    dispatch(createNewEmptyProject());
                    setIsUpdate(false);
                }
            }
        }
    }, [params, open, path, currProjectId]);

    useEffect(() => {
        if (!clients) {
            dispatch(importClients());
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (!!dispatch && !allProjectManagers) {
            dispatch(importAllProjectManagers());
        }
    }, [dispatch, open]);

    const handleClose = () => {
        dispatch(clearProject());
        closeForm();
    };

    const isProjectValid =
        !!project && !!project?.name && project.name.trim() !== '' && !!project.clientId && project.clientId > 0;

    React.useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            alert('uspesno sacuvano');
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleSelectClientForProject = (event: SelectChangeEvent<number>) => {
        const val = event.target.value;

        if (!isNaN(+val) && +val > 0) {
            dispatch(selectClient(+val));
        }
    };

    const handleChangeProjectName = (event: ChangeEvent<HTMLInputElement>) => {
        const val = event.target.value;

        dispatch(updateProjectName(val));
    };

    const handleSelectProjectManagerForProject = (pmId?: string) => {
        if (!!pmId) {
            dispatch(updateProjectProjectManagers(pmId));
        }
    };

    const submit = () => {
        dispatch(saveProject());
    };

    const wordOfTheDay = isUpdate ? 'Izmeni' : 'Kreiraj';

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>{wordOfTheDay} projekat</FormDialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 5 }}>
                    <Grid2 container spacing={2} style={{ maxWidth: '700px', margin: 'auto' }}>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            {(!!currProjectId && currProjectId > 0) ||
                            (!!projectId && !isNaN(+projectId) && +projectId > 0) ? (
                                <Typography variant="body2" sx={{ fontSize: 20 }}>
                                    Klijent - {project?.client?.shortName ?? project?.client?.name ?? '/'}
                                </Typography>
                            ) : (
                                <FormControl fullWidth required error={!project?.clientId || project.clientId <= 0}>
                                    <InputLabel id="demo-simple-select-label">Klijent</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        type="number"
                                        value={project?.clientId ?? ''}
                                        label="Projekat"
                                        onChange={handleSelectClientForProject}
                                    >
                                        {clients?.map((client) => {
                                            return (
                                                <MenuItem
                                                    key={(client?.id ?? Math.random()) + (client?.name ?? '')}
                                                    value={client.id}
                                                >
                                                    {client.shortName ?? client.name ?? ''}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                error={!project?.name || project?.name === ''}
                                required
                                fullWidth
                                name="name"
                                label="Naziv"
                                value={!!project?.name ? project?.name : ''}
                                variant="outlined"
                                onChange={handleChangeProjectName}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <Typography variant="body2" sx={{ fontSize: 20 }}>
                                Projekt menadžeri
                            </Typography>
                            <hr />
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                {allProjectManagers?.map((manager) => {
                                    const selected =
                                        !!manager.id && project?.projectManagersAzObjIds?.includes(manager.id);
                                    return (
                                        <div
                                            key={manager.id}
                                            onClick={() => handleSelectProjectManagerForProject(manager.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Chip
                                                sx={{
                                                    height: 'auto',
                                                    p: 1,
                                                    cursor: !!handleSelectProjectManagerForProject ? 'pointer' : 'auto',
                                                }}
                                                color={!!handleSelectProjectManagerForProject ? 'primary' : 'default'}
                                                variant={selected ? 'filled' : 'outlined'}
                                                label={
                                                    <div>
                                                        <Typography>{manager?.displayName ?? '/'}</Typography>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    );
                                })}
                            </Box>
                        </Grid2>
                    </Grid2>
                </Box>

                {!isProjectValid && (
                    <Box sx={{ mb: 3 }}>
                        <Typography color="error">*unesite neophodne podatke za projekat</Typography>
                    </Box>
                )}

                <FormActions
                    onCancel={handleClose}
                    onSubmit={submit}
                    submitLabel="Sačuvaj"
                    disabled={!isProjectValid || saveStatus === SaveEntityStatus.Pending}
                    pending={saveStatus === SaveEntityStatus.Pending}
                    inGrid={false}
                />
            </DialogContent>
        </ResponsiveDialog>
    );
}
